import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { ReactNode } from 'react';

import { Spacing } from 'chatie-design-system';

import Avatar, { AvatarSize } from '@components/common/Avatar';
import Badge, { useBadgePresets } from '@components/common/Badge';
import { CrownRank } from '@components/common/ListItem';
import Nickname from '@components/user/Nickname';

import { IFanUser, ISimpleUser } from '@model';
import { useFan } from '@utils/hook';

const userCSS = css`
  display: flex;
  align-items: center;
`;

const descriptionCSS = (isActive: boolean) => (theme: Theme) =>
  css`
    margin-left: ${Spacing.medium}px;
    min-width: 0;

    h5 {
      span + span {
        margin-left: ${Spacing.xxsmall}px;
      }

      ${!isActive &&
      css`
        color: ${theme.var.gray400};
      `}
    }
  `;

type Props = {
  label?: ReactNode;
  children?: ReactNode;
  targetUserId?: number;
  rank?: number;
};

const User = ({
  label,
  targetUserId,
  children,
  rank,
  ...user
}: (
  | (ISimpleUser & { joined?: string | undefined })
  | (IFanUser & { isActive?: boolean | undefined })
) &
  Props) => {
  const { id, isActive = undefined, joined = undefined } = user;

  const { t } = useTranslation();
  const day = 1000 * 60 * 60 * 24;
  const isNew = joined && new Date(joined).getTime() + day > Date.now();

  const { data: fan } = useFan(id, targetUserId);

  const { fan: fanBadge } = useBadgePresets();
  const { new: newbadge } = useBadgePresets();

  if (isActive === false) {
    return <InactiveUser />;
  }

  return (
    <div css={userCSS} data-test="User">
      {rank && <CrownRank rank={rank} />}
      <Avatar size={AvatarSize.medium} {...user} />
      <div css={descriptionCSS(true)}>
        <h5 className="align-middle">
          {label}
          {isNew && <Badge text={t('N')} {...newbadge} />}
          {fan?.isFan && (
            <Badge text={fan.fanClubName ?? t('팬')} {...fanBadge} />
          )}
          <Nickname {...user} />
        </h5>
        {children}
      </div>
    </div>
  );
};

export default User;

export const InactiveUser = ({ children }: Props) => {
  const { t } = useTranslation();

  return (
    <div css={userCSS}>
      <Avatar size={AvatarSize.medium} />
      <div css={descriptionCSS(false)}>
        <h5 className="ellipsis align-middle">
          <span className="nickname">{t('이미 탈퇴한 유저입니다 😢')}</span>
        </h5>
        {children}
      </div>
    </div>
  );
};
